import { lazy, type ComponentType, type LazyExoticComponent } from "react";
import type { SlotName } from "zudoku";
import {
  isInjectionTarget,
  TARGETS,
  type InjectionTarget,
} from "./targets.js";

/** Matches …/{target}/{id}/{slot}.{md,mdx} (directory name is not hardcoded). */
const INJECTION_PATH = /\/([^/]+)\/([^/]+)\/([^/]+)\.(?:mdx?|md)$/;

type LazyInjection = LazyExoticComponent<ComponentType>;

/**
 * Nested map: slot → target → id → lazy MDX component.
 * Path shape: {target}/{id}/{slot}.mdx
 */
export type InjectionsBySlot = Map<
  SlotName,
  Map<InjectionTarget, Map<string, LazyInjection>>
>;

function parseInjectionPath(
  filePath: string,
): { target: string; id: string; slot: string } | null {
  const match = filePath.match(INJECTION_PATH);
  if (!match) return null;
  return { target: match[1], id: match[2], slot: match[3] };
}

export function discoverInjections(
  loaders: Record<string, () => Promise<{ default: ComponentType }>>,
): InjectionsBySlot {
  const bySlot: InjectionsBySlot = new Map();
  const seen = new Set<string>();

  for (const [filePath, load] of Object.entries(loaders)) {
    const parsed = parseInjectionPath(filePath);
    if (!parsed) continue;

    const { target, id, slot } = parsed;

    if (!isInjectionTarget(target)) {
      console.warn(
        `[slot-injection] Ignoring ${filePath}: unknown target "${target}"`,
      );
      continue;
    }

    const targetDef = TARGETS[target];
    if (!targetDef.validSlots.has(slot as SlotName)) {
      console.warn(
        `[slot-injection] Ignoring ${filePath}: "${slot}" is not a valid slot for target "${target}"`,
      );
      continue;
    }

    const slotName = slot as SlotName;
    const key = `${target}:${id}:${slotName}`;
    if (seen.has(key)) {
      console.warn(
        `[slot-injection] Duplicate injection for ${key}, keeping first`,
      );
      continue;
    }
    seen.add(key);

    const Lazy = lazy(async () => {
      const mod = await load();
      return { default: mod.default };
    });

    let byTarget = bySlot.get(slotName);
    if (!byTarget) {
      byTarget = new Map();
      bySlot.set(slotName, byTarget);
    }

    let byId = byTarget.get(target);
    if (!byId) {
      byId = new Map();
      byTarget.set(target, byId);
    }
    byId.set(id, Lazy);
  }

  return bySlot;
}
